/**
 * InstallAppButton — single visible install entry-point.
 *
 * Click behaviour:
 *   1. If `beforeinstallprompt` was captured at boot (Chrome / Edge / Android-
 *      Chrome / Brave-without-shields-blocking), fire the OS install dialog
 *      directly via the captured event. Synchronous call — preserves the
 *      user-gesture activation window browsers require.
 *   2. Otherwise (Safari, Firefox, Brave-with-shields, or any second visit
 *      where the event was already consumed), open the InstallHelpModal —
 *      a single browser-detected explainer with one clear instruction.
 *
 * Self-hides when display-mode is standalone (app already installed).
 *
 * Note: the old auto-banner was removed alongside this redesign. Install
 * intent is now always user-initiated — no more "did something just pop up?"
 * mystery for users who don't recognise the bottom-of-screen prompt.
 */
import React, { useEffect, useState } from 'react';
import { Download } from 'lucide-react';
import { Button } from './ui/button';
import { promptInstall, subscribeDeferredPrompt } from '../lib/pwaInstall';
import InstallHelpModal from './InstallHelpModal';

const isStandalone = () => {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator?.standalone === true
  );
};

export const InstallAppButton = ({ className = '', size = 'sm', title = 'Install app' }) => {
  const [standalone, setStandalone] = useState(isStandalone());
  const [hasDeferred, setHasDeferred] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    const mql = window.matchMedia?.('(display-mode: standalone)');
    if (!mql) return undefined;
    const onChange = () => setStandalone(isStandalone());
    mql.addEventListener ? mql.addEventListener('change', onChange) : mql.addListener(onChange);
    return () => {
      mql.removeEventListener ? mql.removeEventListener('change', onChange) : mql.removeListener(onChange);
    };
  }, []);

  useEffect(() => {
    return subscribeDeferredPrompt((p) => setHasDeferred(!!p));
  }, []);

  if (standalone) return null;

  const handleClick = () => {
    // Path 1: native prompt available — fire it synchronously inside the
    // user-gesture window. Browser shows its own install confirmation.
    const result = promptInstall();
    if (result !== null) return;

    // Path 2: no captured event — open the help modal with the user's
    // browser-specific instructions instead of guessing.
    setModalOpen(true);
  };

  return (
    <>
      <Button
        onClick={handleClick}
        variant="outline"
        size={size}
        className={className}
        title={hasDeferred ? 'Install NeuroWave' : title}
        data-testid="install-app-btn"
        aria-label={hasDeferred ? 'Install NeuroWave' : title}
      >
        <Download className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
      </Button>
      <InstallHelpModal open={modalOpen} onOpenChange={setModalOpen} />
    </>
  );
};

export default InstallAppButton;
